/**
 * BIES HOUSE - Modern Cart Drawer
 * Handles all cart drawer functionality
 */

// Debug utility — only logs when window.BIES_DEBUG is truthy
function biesDebug(...args) {
  if (window.BIES_DEBUG) console.log(...args);
}

// Prevent double initialization
if (window.biesCartDrawerInitialized) {
  biesDebug('[BIES Cart Drawer] Already initialized, skipping duplicate');
} else {
  window.biesCartDrawerInitialized = true;
  biesDebug('[BIES Cart Drawer] Script loaded');
}

class BiesCartDrawer {
  // Nombre de la section para la Section Rendering API — usado al pedir
  // sections= en add.js Y al extraer la respuesta. Un solo sitio: un rename
  // de la section se cambia aquí (y en el archivo .liquid).
  static SECTION_ID = 'bies-modern-cart-drawer';

  constructor() {
    this.drawer = null;
    this.overlay = null;
    this.REWARDS_TIERS = [
      { threshold: 4000, giftName: 'Granola Original', shortName: 'Granola', giftVariantId: 52456154431835, realValue: 590 },
      { threshold: 5000, giftName: 'Stevia BIES', shortName: 'BIES', giftVariantId: 52456161771867, realValue: 1450 },
      { threshold: 7000, giftName: 'Batido de Proteínas', shortName: 'Batido', giftVariantId: 52456162066779, realValue: 1950 }
    ];
    this.REWARDS_MAX = 7000;
    this.GIFT_VARIANT_IDS = new Set([52456154431835, 52456161771867, 52456162066779]);

    // ── Concurrencia (rediseño 13-jul, mega-code-review) ──────────────────
    // Cola serie: UNA operación de carrito a la vez. Mata el overshoot del
    // doble-click (dos /cart/change.js concurrentes) y las respuestas fuera
    // de orden que pintaban estados viejos ("se queda estancada").
    this._opQueue = Promise.resolve();
    this._seq = 0;            // epoch monotónico de estados de carrito
    this._renderedSeq = -1;   // último seq efectivamente pintado
    this._loadingCount = 0;   // refcount de setLoading (no toggle suelto)
    this._mutating = false;   // guarda contra reentradas por 'cart:updated' propio

    // ── Caches de recomendaciones (14-jul, seamless) ──────────────────────
    // Sin esto, cada add/remove relanzaba la cascada completa: 1 fetch de
    // /products/{handle}.js POR item + 1 de /recommendations POR item + el
    // fallback — con el bloque en "Cargando..." y salto de layout. Los datos
    // son estables durante la vida de la página → cache en memoria.
    this._productTypeCache = new Map();   // handle → tipo inferido
    this._recsCache = new Map();          // product_id → recs Shopify API
    this._allProductsCache = null;        // /products.json (pool de fallback)
    this._recoRunId = 0;                  // carrera: solo pinta el run más nuevo
    this._lastRenderedRecoIds = '';       // IDs pintados (skip swap idéntico)
    this._lastRecoKey = null;             // set de productos del último run de recos
                                          // (null, NO '': un carrito vacío produce
                                          // recoKey '' y debe correr la primera vez)

    this.init();
  }

  // Encola una operación de carrito. Las ops corren en serie: la siguiente
  // no empieza hasta que la anterior (con su render) termina.
  _serial(taskFn) {
    const run = () => Promise.resolve()
      .then(() => { this._mutating = true; })
      .then(taskFn)
      .catch((e) => console.error('[BIES Cart Drawer] op error:', e))
      .finally(() => { this._mutating = false; });
    this._opQueue = this._opQueue.then(run, run);
    return this._opQueue;
  }

  // Fetch autoritativo del carrito con número de secuencia (para descartar
  // renders fuera de orden en renderCart).
  async _authoritativeCart() {
    const cart = await (await fetch('/cart.js')).json();
    cart.__seq = ++this._seq;
    return cart;
  }

  // Extrae el carrito autoritativo del JSON embebido que la Section Rendering
  // API devuelve junto a /cart/add.js (script#bies-cart-json-data en la
  // section) — colapsa add+read a UN solo round-trip. SOLO se usa el JSON;
  // NUNCA el HTML del server: el markup de items de la section es el shell
  // simplificado del first paint (sin data-item-key, sin precios, sin manejo
  // de regalos) — swapearlo fue lo que rompió el carrito el 13-jul. Devuelve
  // null si algo no cuadra → el caller cae al fetch de /cart.js de siempre.
  // Carrito autoritativo de una respuesta de add.js: JSON embebido si vino,
  // fetch clásico a /cart.js si no. Único punto de resolución para los paths
  // de add (form interceptor + addRecommendedProduct).
  async _cartFromAddResponse(responseData) {
    return this._cartFromSectionsResponse(responseData) || await this._authoritativeCart();
  }

  _cartFromSectionsResponse(responseData) {
    try {
      const html = responseData && responseData.sections && responseData.sections[BiesCartDrawer.SECTION_ID];
      if (!html) return null;
      // DOMParser: no ejecuta scripts NI dispara carga de <img> (un div
      // desconectado con innerHTML sí precarga imágenes).
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const scriptEl = doc.getElementById('bies-cart-json-data');
      if (!scriptEl) return null;
      const cart = JSON.parse(scriptEl.textContent);
      if (!cart || typeof cart.item_count !== 'number' || !Array.isArray(cart.items)) return null;
      cart.__seq = ++this._seq;
      return cart;
    } catch (e) {
      biesDebug('[BIES Cart Drawer] sections JSON extract failed — fallback a /cart.js', e);
      return null;
    }
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    biesDebug('[BIES Cart Drawer] Setup starting...');
    this.drawer = document.getElementById('bies-cart-drawer');
    this.overlay = document.getElementById('bies-cart-drawer-overlay');

    if (!this.drawer) {
      console.error('[BIES Cart Drawer] CRITICAL: Drawer element #bies-cart-drawer not found!');
      return;
    }

    biesDebug('[BIES Cart Drawer] Drawer element found, binding events...');
    this.bindEvents();

    // Defer cart pipeline to idle time — drawer is hidden, no need to block page load
    this._cartReady = false;
    this._lastCartUpdate = 0;
    const runUpdate = () => {
      this.updateCart().then(() => { this._cartReady = true; });
    };
    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(runUpdate, { timeout: 2000 });
    } else {
      setTimeout(runUpdate, 100);
    }

    biesDebug('[BIES Cart Drawer] Setup complete');
  }

  bindEvents() {
    biesDebug('[BIES Cart Drawer] bindEvents() called');

    // Close buttons
    const closeBtns = this.drawer.querySelectorAll('[data-cart-drawer-close]');
    closeBtns.forEach(btn => {
      btn.addEventListener('click', () => this.close());
    });

    // Overlay click to close
    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }

    // Store reference to this for event handlers
    const self = this;

    // Unified click handler for all cart drawer interactions
    biesDebug('[BIES Cart Drawer] Adding click handler to drawer');
    this.drawer.addEventListener('click', function(e) {
      biesDebug('[BIES Cart Drawer] Click detected on:', e.target.tagName, e.target.className);
      // Handle quantity buttons
      const qtyBtn = e.target.closest('[data-qty-action]');
      if (qtyBtn) {
        e.preventDefault();
        e.stopPropagation();
        const action = qtyBtn.getAttribute('data-qty-action');
        const lineIndex = qtyBtn.getAttribute('data-line-index');
        biesDebug('[BIES Cart Drawer] Qty button clicked:', action, 'line:', lineIndex);
        self._serial(() => self.updateQuantity(lineIndex, action));
        return;
      }

      // Handle remove item buttons
      const removeBtn = e.target.closest('[data-remove-item]');
      if (removeBtn) {
        e.preventDefault();
        e.stopPropagation();
        const lineIndex = removeBtn.getAttribute('data-line-index');
        biesDebug('[BIES Cart Drawer] Remove button clicked for line:', lineIndex);
        self._serial(() => self.removeItem(lineIndex));
        return;
      }

      // Handle add recommended product buttons
      const addBtn = e.target.closest('[data-add-recommended]');
      if (addBtn) {
        e.preventDefault();
        e.stopPropagation();
        const variantId = addBtn.getAttribute('data-variant-id');
        biesDebug('[BIES Cart Drawer] Add recommended clicked:', variantId);
        self._serial(() => self.addRecommendedProduct(variantId, addBtn));
        return;
      }
    });

    // Listen for cart updates from OTHER sources (p.ej. free-shipping bar).
    // Se encola, y se ignora si viene de una mutación propia en curso
    // (nuestro processCart ya reconcilia) — evita la cascada concurrente.
    document.addEventListener('cart:updated', () => {
      if (this._mutating) return;
      this._serial(() => this.updateCart());
    });

    // ESC key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) {
        this.close();
      }
    });
  }

  open(options = {}) {
    if (!this.drawer) return;

    this._animateItemsOnce = true; // los items animan su entrada solo al abrir (fix glitch 13-jul)
    this.drawer.classList.add('is-open');
    if (this.overlay) {
      this.overlay.classList.add('is-visible');
    }

    // Prevent body scroll
    document.body.style.overflow = 'hidden';

    // Lazy-render payment icons (~40KB of inline SVG) on first open only.
    // Defensive: never let this break drawer opening if markup is missing.
    this._renderPaymentIcons();

    // Update cart data when opening (skip if caller already did it, or if recently updated)
    if (!options.skipUpdate) {
      const cacheAge = Date.now() - (this._lastCartUpdate || 0);
      if (!this._cartReady || cacheAge > 30000) {
        this.updateCart().then(() => { this._cartReady = true; });
      }
    }

    // Analytics
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'cart_drawer_opened'
      });
    }
  }

  /**
   * Clones the payment-icon SVGs from their <template> into the drawer
   * the first time it opens. Idempotent (no-ops if already rendered).
   * Defensive: swallows errors so a markup change can never block the
   * drawer from opening.
   */
  _renderPaymentIcons() {
    if (this._paymentIconsRendered) return;
    try {
      const target = document.getElementById('bies-cart-drawer-payment-icons');
      const tpl = document.getElementById('bies-cart-drawer-payment-icons-template');
      if (!target || !tpl || !tpl.content) return;
      target.appendChild(tpl.content.cloneNode(true));
      this._paymentIconsRendered = true;
    } catch (error) {
      biesDebug('[BIES Cart Drawer] Payment icons lazy-render failed (non-fatal):', error);
    }
  }

  close() {
    if (!this.drawer) return;

    this.drawer.classList.remove('is-open');
    if (this.overlay) {
      this.overlay.classList.remove('is-visible');
    }

    // Restore body scroll
    document.body.style.overflow = '';

    // Analytics
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'cart_drawer_closed'
      });
    }
  }

  isOpen() {
    return this.drawer && this.drawer.classList.contains('is-open');
  }

  async updateCart() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      await this.processCart(cart);
      this._lastCartUpdate = Date.now();
    } catch (error) {
      console.error('[BIES Cart Drawer] Error updating cart:', error);
      this._normalizing = false;
      this._syncingGifts = false;
    }
  }

  /**
   * Process cart data and render. Accepts cart object directly (avoids re-fetching /cart.js).
   * @param {Object} cart - Full cart object (from /cart.js or /cart/change.js response)
   * @param {Object} options
   * @param {boolean} options.skipNormalization - Skip bundle normalization (for simple qty changes on non-bundle items)
   */
  async processCart(cart, { skipNormalization = false } = {}) {
    // Rediseño 13-jul: reconciliación SECUENCIAL — una sola pasada, un solo
    // render con el estado FINAL. Antes disparaba 3 renders (inicial +
    // gift-sync bg + compare-at bg) que se pisaban → flash de imágenes,
    // re-animación y "se queda estancada". Ahora normalize + gifts se
    // resuelven con await ANTES del único renderCart.
    this.setLoading(true);
    try {
      if (cart.__seq == null) cart.__seq = ++this._seq;

      // 1. Normalizar bundles (dedup / qty>1 → tier correcto). Sin guard
      //    booleano global (la cola serie ya garantiza exclusión).
      if (!skipNormalization) {
        let changed = true, attempts = 5;
        while (changed && attempts-- > 0) {
          changed = await this.normalizeBundleQuantities(cart);
          if (changed) cart = await this._authoritativeCart();
        }
      }

      // 2. Sincronizar regalos escalonados (add/remove según umbral) con
      //    AWAIT — no en background. Si cambió, re-leemos el cart real.
      const giftChanged = await this.syncGiftProducts(cart);
      if (giftChanged) cart = await this._authoritativeCart();

      // 3. UN único render con el estado final reconciliado.
      this.renderCart(cart);
      this.updateRewardsBar(cart.total_price);
      document.dispatchEvent(new CustomEvent('bies:cart:updated', { detail: cart }));
    } catch (error) {
      console.error('[BIES Cart Drawer] Error processing cart:', error);
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Auto-normalize bundle items in cart.
   * Detects two scenarios:
   *   1. Same variant added multiple times (qty > 1)
   *   2. Multiple tiers of the same bundle in cart (e.g. tier 1 + tier 2 = 3 units → tier 3)
   * Consolidates into the correct single tier with qty=1, giving the customer the best discount.
   */
  async normalizeBundleQuantities(cart) {
    const bundleMap = window.BIES_VARIANT_TO_BUNDLE || {};
    const bundleVariants = window.BIES_BUNDLE_VARIANTS || {};
    const maxTier = window.BIES_MAX_TIER || 4;
    const tierDiscounts = window.BIES_TIER_DISCOUNTS || {};

    // Group cart items by bundleCode
    const bundleGroups = {};
    for (let i = 0; i < cart.items.length; i++) {
      const item = cart.items[i];
      const bundleInfo = bundleMap[String(item.variant_id)];
      if (bundleInfo) {
        const code = bundleInfo.bundleCode;
        if (!bundleGroups[code]) bundleGroups[code] = [];
        bundleGroups[code].push({
          tier: bundleInfo.tier,
          quantity: item.quantity,
          variantId: String(item.variant_id)
        });
      }
    }

    // Check each bundle group
    for (const code of Object.keys(bundleGroups)) {
      const group = bundleGroups[code];

      // Calculate total units across all items of this bundle
      let totalUnits = 0;
      for (const entry of group) {
        totalUnits += entry.tier * entry.quantity;
      }

      // Needs normalization if: multiple line items OR any item with qty > 1
      const needsNormalization = group.length > 1 || group[0].quantity > 1;
      if (!needsNormalization) continue;

      const targetTier = Math.min(totalUnits, maxTier);
      const tiers = bundleVariants[code];
      if (!tiers || !tiers[targetTier]) continue;

      biesDebug('[BIES Cart Drawer] Auto-normalizing:', code,
        'items=' + group.length, 'totalUnits=' + totalUnits, '→ tier=' + targetTier);

      // Remove all variants of this bundle at once using /cart/update.js
      const updates = {};
      for (const entry of group) {
        updates[entry.variantId] = 0;
      }
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: updates })
      });

      // Add the correct tier at qty=1
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: tiers[targetTier], quantity: 1 }] })
      });

      const discount = tierDiscounts[targetTier] || 0;
      if (discount > 0) {
        this.showNotification('¡Mejorado a pack ' + targetTier + 'x (-' + discount + '%)!');
      }

      return true; // Re-fetch cart before processing other bundles
    }

    return false;
  }

  async updateCompareAtPrices(items) {
    // Initialize cache if it doesn't exist
    if (!window.biesCompareAtPrices) {
      window.biesCompareAtPrices = {};
    }

    // Collect items that need fetching (not yet cached)
    const uncachedItems = items.filter(item => window.biesCompareAtPrices[item.variant_id] === undefined);
    if (uncachedItems.length === 0) return false;

    // Deduplicate by handle (one product fetch covers all its variants)
    const handlesSeen = new Set();
    const uniqueItems = uncachedItems.filter(item => {
      if (handlesSeen.has(item.handle)) return false;
      handlesSeen.add(item.handle);
      return true;
    });

    // Fetch all products in parallel
    const fetches = uniqueItems.map(async (item) => {
      try {
        const productResponse = await fetch(`/products/${item.handle}.js`);
        const productData = await productResponse.json();

        // Cache all variants from this product at once
        for (const variant of productData.variants) {
          if (window.biesCompareAtPrices[variant.id] === undefined) {
            window.biesCompareAtPrices[variant.id] = variant.compare_at_price || 0;
          }
        }
        biesDebug('[BIES Cart Drawer] Cached compare_at_prices for', item.handle);
      } catch (error) {
        biesDebug('[BIES Cart Drawer] Could not fetch compare_at_price for', item.handle, error);
        // Mark uncached variants for this item so we don't retry endlessly
        for (const uncached of uncachedItems.filter(i => i.handle === item.handle)) {
          window.biesCompareAtPrices[uncached.variant_id] = 0;
        }
      }
    });

    await Promise.all(fetches);
    return true;
  }

  async syncGiftProducts(cart) {
    // Frame: cart object from /cart.js, GIFT_VARIANT_IDS populated, REWARDS_TIERS configured
    biesDebug('[BIES Gifts] syncGiftProducts called — cart total:', cart.total_price, 'cents, items:', cart.item_count);

    // Step 1: Identify which gift variants are currently in cart
    const currentGiftIds = new Set();
    for (const item of cart.items) {
      if (this.GIFT_VARIANT_IDS.has(item.variant_id)) {
        currentGiftIds.add(item.variant_id);
      }
    }
    biesDebug('[BIES Gifts] Current gifts in cart:', [...currentGiftIds]);

    // Step 2: Calculate which gifts SHOULD be in cart based on thresholds
    const total = cart.total_price;
    const shouldHave = new Set();
    for (const tier of this.REWARDS_TIERS) {
      if (total >= tier.threshold) {
        shouldHave.add(tier.giftVariantId);
        biesDebug('[BIES Gifts] Threshold met:', tier.giftName, '(' + tier.threshold + ' cents)');
      }
    }

    const toAdd = [...shouldHave].filter(id => !currentGiftIds.has(id));
    const toRemove = [...currentGiftIds].filter(id => !shouldHave.has(id));

    if (toAdd.length === 0 && toRemove.length === 0) {
      biesDebug('[BIES Gifts] No changes needed');
      return false;
    }

    biesDebug('[BIES Gifts] To add:', toAdd, '| To remove:', toRemove);
    let changed = false;

    // Step 3: Remove gifts that shouldn't be there
    if (toRemove.length > 0) {
      try {
        const updates = {};
        for (const variantId of toRemove) {
          updates[String(variantId)] = 0;
        }
        const removeResponse = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates })
        });
        if (!removeResponse.ok) {
          const errorBody = await removeResponse.text();
          console.error('[BIES Gifts] Remove failed:', removeResponse.status, errorBody);
        } else {
          biesDebug('[BIES Gifts] Removed', toRemove.length, 'gift(s)');
          changed = true;
        }
      } catch (err) {
        console.error('[BIES Gifts] Remove error:', err);
      }
    }

    // Step 4: Add gifts that should be there (one by one for better error visibility)
    for (const variantId of toAdd) {
      const tierInfo = this.REWARDS_TIERS.find(t => t.giftVariantId === variantId);
      const giftName = tierInfo ? tierInfo.giftName : 'Unknown';
      biesDebug('[BIES Gifts] Adding gift:', giftName, '(variant:', variantId, ')');

      try {
        const addResponse = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{
              id: String(variantId),
              quantity: 1,
              properties: { '_gift': 'true' }
            }]
          })
        });

        if (!addResponse.ok) {
          const errorBody = await addResponse.text();
          console.error('[BIES Gifts] ADD FAILED for', giftName, '— Status:', addResponse.status, '— Body:', errorBody);

          // Retry once after short delay (Shopify propagation)
          biesDebug('[BIES Gifts] Retrying', giftName, 'in 500ms...');
          await new Promise(r => setTimeout(r, 500));
          const retryResponse = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              items: [{
                id: String(variantId),
                quantity: 1,
                properties: { '_gift': 'true' }
              }]
            })
          });
          if (!retryResponse.ok) {
            const retryBody = await retryResponse.text();
            console.error('[BIES Gifts] RETRY ALSO FAILED for', giftName, '—', retryResponse.status, retryBody);
          } else {
            biesDebug('[BIES Gifts] Retry SUCCESS for', giftName);
            changed = true;
          }
        } else {
          const addData = await addResponse.json();
          biesDebug('[BIES Gifts] Added', giftName, '— Response:', addData.items ? addData.items.length + ' item(s)' : 'ok');
          changed = true;
        }
      } catch (err) {
        console.error('[BIES Gifts] Network error adding', giftName, ':', err);
      }
    }

    biesDebug('[BIES Gifts] Sync complete — changed:', changed);
    return changed;
  }

  renderCart(cart) {
    biesDebug('[BIES Cart Drawer] renderCart called with', cart.item_count, 'items');

    // Epoch guard (13-jul): descarta renders de estados VIEJOS que lleguen
    // fuera de orden (gift-sync tardío, 'cart:updated' externo). El estado
    // más nuevo nunca se pisa con uno anterior.
    if (cart.__seq != null) {
      if (cart.__seq < this._renderedSeq) {
        biesDebug('[BIES Cart Drawer] stale render dropped', cart.__seq, '<', this._renderedSeq);
        return;
      }
      this._renderedSeq = cart.__seq;
    }

    const itemsContainer = document.getElementById('bies-cart-items');
    const emptyState = document.getElementById('bies-cart-empty');
    const bodyContent = document.getElementById('bies-cart-body-content');
    const subtotalValue = document.getElementById('bies-cart-subtotal-value');
    const checkoutBtn = document.getElementById('bies-cart-checkout-btn');

    if (!itemsContainer) {
      console.error('[BIES Cart Drawer] itemsContainer NOT FOUND! Cannot render cart.');
      return;
    }

    // Subtotal (siempre in-place)
    if (subtotalValue) {
      subtotalValue.textContent = this.formatMoney(cart.total_price);
    }

    // Empty state
    if (cart.item_count === 0) {
      if (emptyState) emptyState.style.display = 'flex';
      if (bodyContent) bodyContent.style.display = 'none';
      if (checkoutBtn) checkoutBtn.disabled = true;
      this._lastStructureKey = '';
      itemsContainer.innerHTML = '';
      this.updateRecommendedProducts(cart);
      return;
    }

    if (emptyState) emptyState.style.display = 'none';
    if (bodyContent) bodyContent.style.display = 'flex';
    if (checkoutBtn) checkoutBtn.disabled = false;

    // CRÍTICO: el line-index de /cart/change.js es 1-based sobre cart.items
    // COMPLETO (regalos incluidos). Por eso mapeamos sobre cart.items entero
    // y devolvemos '' para los regalos (renderCartItem ya lo hace) en vez de
    // pre-filtrar — pre-filtrar desalineaba el índice cuando había un regalo
    // en el carrito y las acciones +/- operaban sobre la línea equivocada.
    const allItems = cart.items;

    // ── RENDER DIFFING (13-jul, mega-code-review) ────────────────────────
    // Reconstruir el innerHTML SOLO cuando cambia el CONJUNTO/orden de line
    // items visibles. Para un simple +/- de cantidad (o swap de tier) el
    // conjunto NO cambia → parcheamos cantidad/precio/badge IN-PLACE, sin
    // recrear el DOM ni las <img> (esto mataba con un flash la UI optimista
    // un beat después del cambio suave = el "glitch estancado").
    const structureKey = allItems
      .filter(i => !this.GIFT_VARIANT_IDS.has(i.variant_id))
      .map(i => this._itemKey(i)).join(',');
    if (structureKey !== this._lastStructureKey) {
      itemsContainer.innerHTML = allItems.map((it, i) => this.renderCartItem(it, i + 1)).join('');
      this._lastStructureKey = structureKey;
      if (this._animateItemsOnce) {
        this._animateItemsOnce = false;   // apertura: dejamos animar
      } else {
        itemsContainer.querySelectorAll('.bies-cart-item').forEach(n => n.classList.add('bies-item--noenter'));
      }
    } else {
      this._patchItemsInPlace(allItems);   // pasa la lista COMPLETA (line-index real)
    }

    this.updateRecommendedProducts(cart);
  }

  // Actualiza cantidad/precio/badge/estado de los items EXISTENTES sin recrear
  // el DOM. Idempotente con la UI optimista de updateQuantity (mismo nodo,
  // mismo valor) → cero flash, cero re-animación.
  _patchItemsInPlace(allItems) {
    const container = document.getElementById('bies-cart-items');
    if (!container) return;
    const tierDiscounts = window.BIES_TIER_DISCOUNTS || { 1: 0, 2: 10, 3: 15, 4: 20 };
    const bundleMap = window.BIES_VARIANT_TO_BUNDLE || {};
    const maxTier = window.BIES_MAX_TIER || 4;

    // Itera sobre cart.items COMPLETO para que lineIndex = índice real 1-based
    // (el que espera /cart/change.js). Los regalos se saltan pero SÍ consumen
    // su índice.
    allItems.forEach((item, i) => {
      const lineIndex = i + 1;
      if (this.GIFT_VARIANT_IDS.has(item.variant_id)) return;
      // Match por identidad ESTABLE (bundleCode para bundles), no por variant_id
      // — un swap de tier cambia el variant_id pero es el mismo item.
      const node = container.querySelector(`.bies-cart-item[data-item-key="${this._itemKey(item)}"]`);
      if (!node) return;

      // El variant_id del nodo puede haber cambiado (swap 1x→2x) — re-sincroniza
      // para que los siguientes clicks operen sobre la variante correcta.
      node.setAttribute('data-variant-id', item.variant_id);
      node.setAttribute('data-line-index', lineIndex);
      node.querySelectorAll('[data-line-index]').forEach(el => el.setAttribute('data-line-index', lineIndex));

      const bundleInfo = bundleMap[String(item.variant_id)];
      const isBundle = !!bundleInfo;
      const currentTier = isBundle ? bundleInfo.tier : 0;
      const discountPercent = isBundle ? (tierDiscounts[currentTier] || 0) : this._discountFromTitle(item.product_title);
      const displayQty = isBundle ? currentTier : item.quantity;

      // Título (en bundles cambia entre tiers: "Té Matcha" ↔ "2x Te Matcha…")
      const titleEl = node.querySelector('.bies-cart-item__title');
      if (titleEl && titleEl.textContent !== item.product_title) titleEl.textContent = item.product_title;

      // Imagen — solo si cambió de verdad (evita re-decodificar/flash)
      const img = node.querySelector('.bies-cart-item__image');
      if (img && item.image && img.getAttribute('src') !== item.image) img.setAttribute('src', item.image);

      // Cantidad
      const qtyEl = node.querySelector('.bies-cart-item__qty-value');
      if (qtyEl && qtyEl.textContent != String(displayQty)) qtyEl.textContent = displayQty;

      // Precio de línea
      const priceEl = node.querySelector('.bies-cart-item__price-current');
      const priceStr = this.formatMoney(item.final_line_price);
      if (priceEl && priceEl.textContent !== priceStr) priceEl.textContent = priceStr;

      // Badge de descuento (crear/actualizar/quitar in-place)
      const priceWrap = node.querySelector('.bies-cart-item__price');
      let badge = priceWrap ? priceWrap.querySelector('.bies-cart-item__price-badge') : null;
      if (discountPercent > 0) {
        if (!badge && priceWrap) {
          badge = document.createElement('span');
          badge.className = 'bies-cart-item__price-badge';
          priceWrap.insertBefore(badge, priceWrap.firstChild);
        }
        if (badge) badge.textContent = `-${discountPercent}%`;
      } else if (badge) {
        badge.remove();
      }

      // Botón + deshabilitado al llegar a max tier
      const plusBtn = node.querySelector('[data-qty-action="plus"]');
      if (plusBtn) {
        const disabled = isBundle && currentTier >= maxTier;
        plusBtn.classList.toggle('is-disabled', disabled);
        plusBtn.disabled = disabled;
      }

      // Etiqueta de tier (crear/actualizar/quitar según corresponda)
      let tierLabelEl = node.querySelector('.bies-cart-item__tier-label');
      const tierText = isBundle
        ? (currentTier > 1 ? `${currentTier}x unidades · -${discountPercent}%` : '1 unidad')
        : null;
      if (tierText != null) {
        if (!tierLabelEl) {
          tierLabelEl = document.createElement('span');
          tierLabelEl.className = 'bies-cart-item__tier-label';
          const footer = node.querySelector('.bies-cart-item__footer');
          if (footer) footer.appendChild(tierLabelEl);
        }
        if (tierLabelEl.textContent !== tierText) tierLabelEl.textContent = tierText;
      } else if (tierLabelEl) {
        tierLabelEl.remove();
      }
    });
  }

  _itemKey(item) {
    const bundleMap = window.BIES_VARIANT_TO_BUNDLE || {};
    const bi = bundleMap[String(item.variant_id)];
    return bi ? bi.bundleCode : String(item.variant_id);
  }

  _discountFromTitle(title) {
    const t = title || '';
    if (/^4x\s/i.test(t)) return 20;
    if (/^3x\s/i.test(t)) return 15;
    if (/^2x\s/i.test(t)) return 10;
    return 0;
  }

  renderCartItem(item, lineIndex) {
    // Gift products are hidden from the item list — shown only via rewards bar + summary badge
    if (this.GIFT_VARIANT_IDS.has(item.variant_id)) {
      return '';
    }

    const imageUrl = item.image ? item.image : '';
    const variantTitle = item.variant_title !== 'Default Title' ? item.variant_title : '';
    const finalLinePrice = item.final_line_price;

    // Detect bundle via shared map
    const bundleMap = window.BIES_VARIANT_TO_BUNDLE || {};
    const tierDiscounts = window.BIES_TIER_DISCOUNTS || { 1: 0, 2: 10, 3: 15, 4: 20 };
    const maxTier = window.BIES_MAX_TIER || 4;
    const bundleInfo = bundleMap[String(item.variant_id)];

    let discountPercent = 0;
    let displayQty = item.quantity;
    let isBundle = false;
    let currentTier = 0;
    let isPlusDisabled = false;

    if (bundleInfo) {
      isBundle = true;
      currentTier = bundleInfo.tier;
      discountPercent = tierDiscounts[currentTier] || 0;
      displayQty = currentTier; // Show tier as visual quantity
      isPlusDisabled = currentTier >= maxTier;
    } else {
      // Fallback: detect discount from title for non-mapped bundles
      const title = item.product_title || '';
      if (title.match(/^4x\s/i)) {
        discountPercent = 20;
      } else if (title.match(/^3x\s/i)) {
        discountPercent = 15;
      } else if (title.match(/^2x\s/i)) {
        discountPercent = 10;
      }
    }

    const hasDiscount = discountPercent > 0;

    // Identidad ESTABLE del item para el render-diffing: para bundles es el
    // bundleCode (un +/- cambia el variant_id 1x→2x pero es el MISMO item
    // cambiando de tier → debe parchearse in-place, no reconstruirse); para
    // productos normales es el variant_id.
    const itemKey = isBundle ? bundleInfo.bundleCode : String(item.variant_id);

    biesDebug('[BIES Cart Drawer] Item:', item.product_title, '- Qty:', item.quantity,
      '- Bundle:', isBundle, '- Tier:', currentTier, '- Discount:', discountPercent + '%');

    const tierLabel = isBundle && currentTier > 1
      ? `<span class="bies-cart-item__tier-label">${currentTier}x unidades · -${discountPercent}%</span>`
      : (isBundle ? `<span class="bies-cart-item__tier-label">1 unidad</span>` : '');

    return `
      <div class="bies-cart-item" data-line-index="${lineIndex}" data-variant-id="${item.variant_id}" data-item-key="${itemKey}">
        ${imageUrl ? `
          <img
            src="${imageUrl}"
            alt="${this.escapeHtml(item.product_title)}"
            class="bies-cart-item__image"
            loading="lazy"
          >
        ` : ''}

        <div class="bies-cart-item__details">
          <h3 class="bies-cart-item__title">${this.escapeHtml(item.product_title)}</h3>
          ${variantTitle ? `
            <p class="bies-cart-item__variant">${this.escapeHtml(variantTitle)}</p>
          ` : ''}

          <div class="bies-cart-item__footer">
            <div class="bies-cart-item__quantity">
              <button
                class="bies-cart-item__qty-btn"
                data-qty-action="minus"
                data-line-index="${lineIndex}"
                aria-label="Disminuir cantidad"
              >
                −
              </button>
              <span class="bies-cart-item__qty-value">${displayQty}</span>
              <button
                class="bies-cart-item__qty-btn${isPlusDisabled ? ' is-disabled' : ''}"
                data-qty-action="plus"
                data-line-index="${lineIndex}"
                aria-label="Aumentar cantidad"
                ${isPlusDisabled ? 'disabled' : ''}
              >
                +
              </button>
            </div>
            ${tierLabel}

            <button
              class="bies-cart-item__remove"
              data-remove-item
              data-line-index="${lineIndex}"
              aria-label="Eliminar producto"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="3 6 5 6 21 6"></polyline>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
              </svg>
            </button>
          </div>
        </div>

        <div class="bies-cart-item__price">
          ${hasDiscount ? `
            <span class="bies-cart-item__price-badge">-${discountPercent}%</span>
          ` : ''}
          <span class="bies-cart-item__price-current">${this.formatMoney(finalLinePrice)}</span>
        </div>
      </div>
    `;
  }

  renderGiftItem(item) {
    const comparePrice = window.biesCompareAtPrices?.[item.variant_id] || 0;
    const giftTitle = item.product_title.replace('REGALO: ', '');

    return `
      <div class="bies-cart-item bies-cart-item--gift-compact">
        <svg class="bies-cart-item__gift-icon-sm" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6DB284" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M20 12v10H4V12"/><path d="M2 7h20v5H2z"/><path d="M12 22V7"/><path d="M12 7H7.5a2.5 2.5 0 010-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 000-5C13 2 12 7 12 7z"/>
        </svg>
        <span class="bies-cart-item__gift-name">${this.escapeHtml(giftTitle)}</span>
        <span class="bies-cart-item__gift-tag">GRATIS</span>
        ${comparePrice > 0 ? `<span class="bies-cart-item__gift-value">(val. ${this.formatMoney(comparePrice)})</span>` : ''}
      </div>
    `;
  }

  async updateQuantity(lineIndex, action) {
    const item = document.querySelector(`[data-line-index="${lineIndex}"]`);
    if (!item) return;

    // Check if this is a bundle product
    const variantId = item.getAttribute('data-variant-id');
    const bundleMap = window.BIES_VARIANT_TO_BUNDLE || {};
    const bundleInfo = variantId ? bundleMap[String(variantId)] : null;

    if (bundleInfo) {
      // Bundle product → swap tiers instead of changing quantity
      await this.swapBundleTier(lineIndex, variantId, bundleInfo, action);
      return;
    }

    // Non-bundle product → normal quantity change
    const qtyElement = item.querySelector('.bies-cart-item__qty-value');
    if (!qtyElement) return;

    let currentQty = parseInt(qtyElement.textContent);
    let newQty = action === 'plus' ? currentQty + 1 : currentQty - 1;

    // Don't allow quantity less than 0
    if (newQty < 0) return;

    // If quantity is 0, remove the item
    if (newQty === 0) {
      this.removeItem(lineIndex);
      return;
    }

    // Optimistic UI: update qty, line price, and subtotal instantly
    qtyElement.textContent = newQty;

    const priceElement = item.querySelector('.bies-cart-item__price-current');
    const subtotalElement = document.getElementById('bies-cart-subtotal-value');
    let oldLinePrice = null;
    let unitPrice = null;

    if (priceElement) {
      oldLinePrice = this.parseMoney(priceElement.textContent);
      unitPrice = Math.round(oldLinePrice / currentQty);
      priceElement.textContent = this.formatMoney(unitPrice * newQty);
    }
    if (subtotalElement && unitPrice !== null) {
      const oldSubtotal = this.parseMoney(subtotalElement.textContent);
      const newSubtotal = oldSubtotal + unitPrice * (newQty - currentQty);
      subtotalElement.textContent = this.formatMoney(newSubtotal);
      // Update rewards bar optimistically
      this.updateRewardsBar(newSubtotal);
    }

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          line: lineIndex,
          quantity: newQty
        })
      });

      if (!response.ok) throw new Error('Failed to update quantity');

      // Use response directly (change.js returns full cart) — skip normalization for non-bundle items
      const cart = await response.json();
      await this.processCart(cart, { skipNormalization: true });

      // Analytics
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'cart_quantity_updated',
          quantity: newQty,
          line_index: lineIndex
        });
      }

    } catch (error) {
      console.error('[BIES Cart Drawer] Error updating quantity:', error);
      // Revert optimistic UI on error
      qtyElement.textContent = currentQty;
      if (priceElement && oldLinePrice !== null) priceElement.textContent = this.formatMoney(oldLinePrice);
      if (subtotalElement) {
        const revertSubtotal = this.parseMoney(subtotalElement.textContent) - unitPrice * (newQty - currentQty);
        subtotalElement.textContent = this.formatMoney(revertSubtotal);
        this.updateRewardsBar(revertSubtotal);
      }
      this.showNotification('Error al actualizar cantidad', 'error');
    }
  }

  /**
   * Swap between bundle tiers (remove current tier, add new tier)
   */
  async swapBundleTier(lineIndex, currentVariantId, bundleInfo, action) {
    const maxTier = window.BIES_MAX_TIER || 4;
    const minTier = window.BIES_MIN_TIER || 1;
    const bundleVariants = window.BIES_BUNDLE_VARIANTS || {};
    const tierDiscounts = window.BIES_TIER_DISCOUNTS || {};

    const currentTier = bundleInfo.tier;
    const bundleCode = bundleInfo.bundleCode;
    const newTier = action === 'plus' ? currentTier + 1 : currentTier - 1;

    biesDebug('[BIES Cart Drawer] Bundle swap:', bundleCode, 'tier', currentTier, '→', newTier);

    // At max tier and pressing +, do nothing
    if (newTier > maxTier) {
      biesDebug('[BIES Cart Drawer] Already at max tier');
      return;
    }

    // Below min tier, remove item from cart
    if (newTier < minTier) {
      biesDebug('[BIES Cart Drawer] Below min tier, removing item');
      this.removeItem(lineIndex);
      return;
    }

    // Get new variant ID
    const tiers = bundleVariants[bundleCode];
    if (!tiers || !tiers[newTier]) {
      console.error('[BIES Cart Drawer] No variant found for', bundleCode, 'tier', newTier);
      this.showNotification('Error: tier no disponible', 'error');
      return;
    }

    const newVariantId = tiers[newTier];

    try {
      this.setLoading(true);

      // Step 1: Remove current item (set quantity to 0)
      const removeResponse = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          line: parseInt(lineIndex, 10),
          quantity: 0
        })
      });

      if (!removeResponse.ok) {
        throw new Error('Failed to remove current tier');
      }

      // Step 2: Add new tier variant (quantity 1)
      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{
            id: newVariantId,
            quantity: 1
          }]
        })
      });

      if (!addResponse.ok) {
        // Rollback: re-add the original variant
        console.error('[BIES Cart Drawer] Add failed, rolling back...');
        await this.rollbackBundleSwap(currentVariantId);
        this.showNotification('Error al cambiar pack, restaurado original', 'error');
        await this.updateCart();
        return;
      }

      // Step 3: Refresh cart — add.js doesn't return full cart, so fetch + process
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      await this.processCart(cart);

      // Notification
      const discount = tierDiscounts[newTier] || 0;
      if (discount > 0) {
        this.showNotification(`Cambiado a pack ${newTier}x (-${discount}%)`);
      } else {
        this.showNotification('Cambiado a 1 unidad');
      }

      // Analytics
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'bundle_tier_swapped',
          bundle_code: bundleCode,
          from_tier: currentTier,
          to_tier: newTier,
          discount_percent: discount
        });
      }

      // Trigger free shipping widget update (cart already fetched above)
      if (window.updateFreeShippingBar) {
        window.updateFreeShippingBar(cart.total_price);
      }

    } catch (error) {
      console.error('[BIES Cart Drawer] Error swapping bundle tier:', error);
      this.showNotification('Error al cambiar cantidad', 'error');
      await this.updateCart();
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Rollback: re-add the original variant if add of new tier fails
   */
  async rollbackBundleSwap(originalVariantId) {
    try {
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{
            id: originalVariantId,
            quantity: 1
          }]
        })
      });
      biesDebug('[BIES Cart Drawer] Rollback successful, re-added variant:', originalVariantId);
    } catch (rollbackError) {
      console.error('[BIES Cart Drawer] Rollback FAILED:', rollbackError);
    }
  }

  async removeItem(lineIndex) {
    biesDebug('[BIES Cart Drawer] Removing item from line:', lineIndex);

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          line: lineIndex,
          quantity: 0
        })
      });

      if (!response.ok) {
        throw new Error('Failed to remove item');
      }

      // Use response directly (change.js returns full cart) — skip normalization
      const cart = await response.json();
      await this.processCart(cart, { skipNormalization: true });

      this.showNotification('Producto eliminado del carrito');

      // Analytics
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'cart_item_removed',
          line_index: lineIndex
        });
      }

    } catch (error) {
      console.error('[BIES Cart Drawer] Error removing item:', error);
      this.showNotification('Error al eliminar producto', 'error');
    }
  }

  async addRecommendedProduct(variantId, button) {
    if (!variantId) return;

    const originalText = button.innerHTML;
    button.disabled = true;
    button.innerHTML = 'Añadiendo...';

    try {
      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{
            id: variantId,
            quantity: 1
          }],
          sections: BiesCartDrawer.SECTION_ID
        })
      });

      if (!addResponse.ok) throw new Error('Failed to add product');

      // (14-jul) JSON embebido de la section = carrito completo en la misma
      // respuesta; fallback al fetch clásico (ahora con __seq — antes este
      // path pintaba sin epoch y podía perder la protección de orden).
      const cart = await this._cartFromAddResponse(await addResponse.json());
      await this.processCart(cart);

      this.showNotification('¡Producto añadido!');

      // Analytics
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'cart_recommended_added',
          variant_id: variantId
        });
      }

    } catch (error) {
      console.error('[BIES Cart Drawer] Error adding recommended product:', error);
      this.showNotification('Error al añadir producto', 'error');
      button.innerHTML = originalText;
      button.disabled = false;
    }
  }

  updateRewardsBar(totalPrice) {
    const rewardsSection = document.getElementById('bies-cart-rewards');
    const progressFill = document.getElementById('bies-rewards-fill');
    const rewardsText = document.getElementById('bies-rewards-text');
    // giftsContainer removed — progress bar milestones are self-explanatory

    if (!rewardsSection || !progressFill) {
      biesDebug('[BIES Rewards] Missing DOM elements:', { rewardsSection: !!rewardsSection, progressFill: !!progressFill });
      return;
    }

    // Update fill bar
    const percentage = Math.min((totalPrice / this.REWARDS_MAX) * 100, 100);
    biesDebug('[BIES Rewards] Bar update:', { totalPrice, max: this.REWARDS_MAX, percentage: percentage.toFixed(1) + '%' });
    progressFill.style.width = `${percentage}%`;

    // Determine unlocked tiers and update milestones
    let unlockedCount = 0;
    let nextTier = null;

    for (let i = 0; i < this.REWARDS_TIERS.length; i++) {
      const tier = this.REWARDS_TIERS[i];
      const milestone = document.getElementById(`bies-milestone-${i + 1}`);

      if (totalPrice >= tier.threshold) {
        unlockedCount = i + 1;
        if (milestone && !milestone.classList.contains('is-unlocked')) {
          milestone.classList.add('is-unlocked');
        }
      } else {
        if (!nextTier) nextTier = tier;
        if (milestone) milestone.classList.remove('is-unlocked');
      }
    }

    // Update dynamic text with compelling copy
    if (rewardsText) {
      if (unlockedCount === this.REWARDS_TIERS.length) {
        rewardsText.innerHTML = '<strong>ENVÍO GRATIS + 3 REGALOS ACUMULABLES</strong> DESBLOQUEADOS';
        rewardsSection.classList.add('is-complete');
      } else {
        rewardsSection.classList.remove('is-complete');
        const remaining = this.formatMoney(nextTier.threshold - totalPrice);

        if (unlockedCount === 0) {
          rewardsText.innerHTML = `TE FALTAN <strong>${remaining}</strong> PARA ENVÍO GRATIS + ${nextTier.giftName.toUpperCase()} GRATIS`;
        } else if (unlockedCount === 1) {
          rewardsText.innerHTML = `1 REGALO ACUMULADO · AÑADE <strong>${remaining}</strong> MÁS PARA ${nextTier.giftName.toUpperCase()}`;
        } else if (unlockedCount === 2) {
          rewardsText.innerHTML = `2 REGALOS ACUMULADOS · SOLO <strong>${remaining}</strong> MÁS PARA ${nextTier.giftName.toUpperCase()}`;
        }
      }
    }

    // Gift badges removed — progress bar milestones are self-explanatory

  }

  /**
   * BIES Smart Recommendations System
   * Uses Shopify Recommendations API + intelligent filtering
   */
  async updateRecommendedProducts(cart) {
    const recommendedContainer = document.getElementById('bies-cart-recommended-items');
    if (!recommendedContainer) {
      biesDebug('[BIES Smart Recs] Recommended container not found');
      return;
    }

    const recommendedSection = recommendedContainer.closest('.bies-cart-drawer__recommended');

    // BIES (17-jun): evitar el "flash" de recomendaciones al cambiar SOLO la cantidad.
    // BIES (14-jul, seamless): NUNCA destruir el contenido visible con un
    // "Cargando..." — las recomendaciones actuales permanecen mientras las
    // nuevas se calculan en background (con caches), y solo hay UN swap al
    // final si el resultado difiere de lo pintado. Esto elimina el bloque en
    // blanco + salto de layout que congelaba el cajón al añadir/quitar items.
    const recoKey = (cart.items || []).map(i => i.product_id).sort().join(',');
    if (recoKey === this._lastRecoKey) return;
    this._lastRecoKey = recoKey;

    // Carrera: si otra mutación relanza recos mientras esta vuela, gana la
    // más nueva — la vieja no pinta.
    const runId = ++this._recoRunId;

    // Get cart product IDs and types
    const cartProductIds = cart.items.map(item => item.product_id);
    // let (no const) para que el catch pueda referenciarla aunque el
    // Promise.all de abajo falle antes de asignarla.
    let cartProductTypes = [];

    try {
      // (14-jul) Tipos y recos por item son independientes hasta el filtrado
      // → las dos olas corren en PARALELO. Con caches fríos (primera carga)
      // esto reduce la latencia del primer cómputo aproximadamente a la mitad.
      const [types, recsArrays] = await Promise.all([
        this.getCartProductTypes(cart.items),
        Promise.all(cart.items.map(item => this.fetchShopifyRecommendations(item.product_id)))
      ]);
      cartProductTypes = types;
      const allRecommendations = recsArrays.flat();

      biesDebug('[BIES Smart Recs] Cart product IDs:', cartProductIds);
      biesDebug('[BIES Smart Recs] Cart product types:', cartProductTypes);
      biesDebug('[BIES Smart Recs] Raw recommendations from API:', allRecommendations.length);

      // Step 2: Deduplicate and filter recommendations
      const seen = new Set();
      const filteredRecommendations = allRecommendations.filter(product => {
        // Skip if already seen
        if (seen.has(product.id)) return false;
        seen.add(product.id);

        // Skip if already in cart
        if (cartProductIds.includes(product.id)) {
          biesDebug('[BIES Smart Recs] Excluding (in cart):', product.title);
          return false;
        }

        // Skip if not available
        if (!product.available) return false;

        // Skip hidden products (tags can be array or string)
        if (product.tags) {
          const tagsLower = Array.isArray(product.tags)
            ? product.tags.map(t => t.toLowerCase()).join(' ')
            : product.tags.toLowerCase();
          if (tagsLower.includes('hidden')) return false;
        }

        // Step 3: SMART FILTER - Exclude same product type
        const productType = this.inferProductType(product);
        if (cartProductTypes.includes(productType)) {
          biesDebug('[BIES Smart Recs] Excluding (same type "' + productType + '"):', product.title);
          return false;
        }

        // Skip packs if user has individual products of that type
        if (this.isPackContainingCartTypes(product, cartProductTypes)) {
          biesDebug('[BIES Smart Recs] Excluding (pack contains cart type):', product.title);
          return false;
        }

        return true;
      });

      biesDebug('[BIES Smart Recs] Filtered recommendations:', filteredRecommendations.length);

      // Step 4: If not enough recommendations, get fallback products
      let finalRecommendations = filteredRecommendations.slice(0, 3);

      if (finalRecommendations.length < 3) {
        biesDebug('[BIES Smart Recs] Not enough recs, fetching fallback...');
        const fallbackProducts = await this.getFallbackRecommendations(
          cartProductIds,
          cartProductTypes,
          finalRecommendations.map(p => p.id)
        );
        finalRecommendations = finalRecommendations.concat(fallbackProducts).slice(0, 3);
      }

      biesDebug('[BIES Smart Recs] Final recommendations:', finalRecommendations.map(p => p.title));

      // Llegó tarde: hay un run más nuevo en vuelo — no pintar estado viejo.
      if (runId !== this._recoRunId) return;

      // Step 5: Render recommendations
      if (finalRecommendations.length === 0) {
        if (recommendedSection) recommendedSection.style.display = 'none';
        this._lastRenderedRecoIds = '';
        return;
      }

      if (recommendedSection) recommendedSection.style.display = 'block';
      // Swap ÚNICO y solo si el resultado difiere de lo pintado — mismo set
      // de recos = cero toques al DOM (cero recarga de <img>, cero reflow).
      const renderedIds = finalRecommendations.map(p => p.id).join(',');
      if (renderedIds !== this._lastRenderedRecoIds) {
        recommendedContainer.innerHTML = finalRecommendations.map(product => this.renderRecommendedProduct(product)).join('');
        this._lastRenderedRecoIds = renderedIds;
      }

      // Analytics
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'smart_recommendations_shown',
          products: finalRecommendations.map(p => p.title),
          cart_types: cartProductTypes
        });
      }

    } catch (error) {
      console.error('[BIES Smart Recs] Error:', error);
      if (runId !== this._recoRunId) return;
      // Permitir reintento en la próxima mutación (el guard recoKey ya quedó
      // seteado y sin esto bloquearía recargas del mismo set tras un fallo).
      this._lastRecoKey = null;
      this._lastRenderedRecoIds = '';
      // Fallback to simple recommendations
      await this.showFallbackRecommendations(cartProductIds, cartProductTypes, recommendedContainer, recommendedSection);
    }
  }

  /**
   * Get product types for all items in cart
   */
  async getCartProductTypes(items) {
    const results = await Promise.all(items.map(async (item) => {
      // Cache por handle (14-jul): el tipo de un producto no cambia durante
      // la vida de la página — sin esto cada mutación re-fetcheaba TODOS los
      // /products/{handle}.js del carrito.
      if (this._productTypeCache.has(item.handle)) {
        return this._productTypeCache.get(item.handle);
      }
      try {
        const response = await fetch(`/products/${item.handle}.js`);
        const product = await response.json();
        const type = this.inferProductType(product);
        this._productTypeCache.set(item.handle, type);
        return type;
      } catch (e) {
        // Fallback por título — NO se cachea (un fetch transitorio fallido
        // no debe fosilizar el tipo inferido de segunda mano).
        return this.inferTypeFromTitle(item.product_title);
      }
    }));
    return [...new Set(results.filter(Boolean))];
  }

  /**
   * Infer product type from product data or title
   */
  inferProductType(product) {
    // First, check if product_type is defined
    if (product.product_type && product.product_type.trim() !== '') {
      return product.product_type.toLowerCase();
    }

    // Otherwise, infer from title
    return this.inferTypeFromTitle(product.title);
  }

  /**
   * Infer product type from title using keywords
   */
  inferTypeFromTitle(title) {
    const titleLower = (title || '').toLowerCase();

    // Order matters - more specific matches first
    if (titleLower.includes('batido de proteína') || titleLower.includes('batido proteína') || titleLower.includes('proteina')) {
      return 'proteina';
    }
    if (titleLower.includes('pancakes') || titleLower.includes('tortitas')) {
      return 'pancakes';
    }
    if (titleLower.includes('granola')) {
      return 'granola';
    }
    if (titleLower.includes('café') || titleLower.includes('cafe')) {
      return 'cafe';
    }
    if (titleLower.includes('matcha')) {
      return 'matcha';
    }
    if (titleLower.includes('bebida instantánea') || titleLower.includes('bebida instantanea')) {
      return 'bebida';
    }
    if (titleLower.includes('shaker')) {
      return 'shaker';
    }
    if (titleLower.includes('endulzante') || titleLower.includes('stevia')) {
      return 'endulzante';
    }
    if (titleLower.includes('boost') || titleLower.includes('pack') || titleLower.includes('rutina') || titleLower.includes('desayuno')) {
      return 'pack';
    }

    return 'otro';
  }

  /**
   * Check if a pack contains products of types already in cart
   */
  isPackContainingCartTypes(product, cartTypes) {
    const titleLower = (product.title || '').toLowerCase();

    // Check if it's a pack
    if (!titleLower.includes('boost') && !titleLower.includes('pack') &&
        !titleLower.includes('rutina') && !titleLower.includes('desayuno')) {
      return false;
    }

    // Check if pack contains cart types
    for (const type of cartTypes) {
      if (type === 'proteina' && (titleLower.includes('proteína') || titleLower.includes('proteina'))) {
        return true;
      }
      if (type === 'cafe' && titleLower.includes('café')) {
        return true;
      }
      if (type === 'granola' && titleLower.includes('granola')) {
        return true;
      }
      if (type === 'shaker' && titleLower.includes('shaker')) {
        return true;
      }
      if (type === 'bebida' && titleLower.includes('bebida')) {
        return true;
      }
    }

    return false;
  }

  /**
   * Fetch recommendations from Shopify API
   */
  async fetchShopifyRecommendations(productId) {
    // Cache por product_id (14-jul): mismas recos durante la sesión de página.
    if (this._recsCache.has(productId)) return this._recsCache.get(productId);
    try {
      const response = await fetch(`/recommendations/products.json?product_id=${productId}&limit=10&intent=related`);
      const data = await response.json();
      const recs = data.products || [];
      this._recsCache.set(productId, recs);
      return recs;
    } catch (error) {
      biesDebug('[BIES Smart Recs] API error for product', productId, error);
      return [];
    }
  }

  /**
   * Get fallback recommendations when API doesn't provide enough
   */
  async getFallbackRecommendations(excludeIds, excludeTypes, alreadyRecommendedIds) {
    try {
      // Cache del pool de fallback (14-jul): /products.json es idéntico
      // durante la sesión de página — un solo fetch.
      if (!this._allProductsCache) {
        const response = await fetch('/products.json?limit=30');
        const data = await response.json();
        this._allProductsCache = data.products || [];
      }

      const allExcludeIds = [...excludeIds, ...alreadyRecommendedIds];

      return this._allProductsCache.filter(product => {
        if (allExcludeIds.includes(product.id)) return false;
        if (!product.available) return false;
        // /products.json devuelve tags como ARRAY (el main path ya maneja
        // ambos — sin este guard, .toLowerCase() sobre array lanza TypeError
        // dentro del filter y el catch dejaba el fallback SIEMPRE vacío).
        if (product.tags) {
          const tagsLower = Array.isArray(product.tags)
            ? product.tags.map(t => t.toLowerCase()).join(' ')
            : product.tags.toLowerCase();
          if (tagsLower.includes('hidden')) return false;
        }

        const type = this.inferProductType(product);
        if (excludeTypes.includes(type)) return false;

        // Also exclude packs
        if (type === 'pack') return false;

        return true;
      }).slice(0, 3);
    } catch (error) {
      biesDebug('[BIES Smart Recs] Fallback error:', error);
      return [];
    }
  }

  /**
   * Show simple fallback recommendations on error
   */
  async showFallbackRecommendations(excludeIds, excludeTypes, container, section) {
    try {
      const products = await this.getFallbackRecommendations(excludeIds, excludeTypes, []);

      if (products.length === 0) {
        if (section) section.style.display = 'none';
        return;
      }

      if (section) section.style.display = 'block';
      container.innerHTML = products.slice(0, 3).map(product => this.renderRecommendedProduct(product)).join('');
    } catch (error) {
      console.error('[BIES Smart Recs] Fallback display error:', error);
      if (section) section.style.display = 'none';
    }
  }

  /**
   * Render a single recommended product
   */
  renderRecommendedProduct(product) {
    const variant = product.variants ? product.variants[0] : product;
    const image = product.images && product.images.length > 0
      ? product.images[0]
      : (product.featured_image || '');
    const price = variant.price || product.price;
    const variantId = variant.id || product.id;

    return `
      <div class="bies-cart-recommended-item">
        ${image ? `
          <img
            src="${typeof image === 'string' ? image : image.src}"
            alt="${this.escapeHtml(product.title)}"
            class="bies-cart-recommended-item__image"
            loading="lazy"
          >
        ` : ''}
        <div class="bies-cart-recommended-item__details">
          <h4 class="bies-cart-recommended-item__title">${this.escapeHtml(product.title)}</h4>
          <p class="bies-cart-recommended-item__price">${this.formatMoney(price)}</p>
        </div>
        <button
          class="bies-cart-recommended-item__add-btn"
          data-add-recommended
          data-variant-id="${variantId}"
        >
          Añadir
        </button>
      </div>
    `;
  }

  // Skeleton del primer add (14-jul): cuando el carrito está vacío y el user
  // añade su primer producto, la apertura optimista NO debe enseñar "tu
  // carrito está vacío" mientras vuela la red. Vive en la clase (no en el
  // interceptor) porque manipula los mismos nodos que renderCart posee —
  // un solo dueño de esos IDs. Devuelve true si lo mostró.
  _showFirstAddSkeleton() {
    const emptyEl = document.getElementById('bies-cart-empty');
    const bodyEl = document.getElementById('bies-cart-body-content');
    const itemsEl = document.getElementById('bies-cart-items');
    if (!emptyEl || !bodyEl || !itemsEl || emptyEl.style.display === 'none') return false;
    emptyEl.style.display = 'none';
    bodyEl.style.display = 'flex';
    itemsEl.innerHTML = '<div class="bies-cart-item bies-cart-item--skeleton" aria-hidden="true"></div>';
    return true;
  }

  // Deshace el skeleton restaurando el empty state — SOLO para el caso de
  // doble fallo de red (add falló Y el refetch de recovery también); en
  // cualquier flujo normal renderCart pisa el skeleton con datos reales.
  _hideFirstAddSkeleton() {
    const emptyEl = document.getElementById('bies-cart-empty');
    const bodyEl = document.getElementById('bies-cart-body-content');
    const itemsEl = document.getElementById('bies-cart-items');
    if (itemsEl) itemsEl.innerHTML = '';
    if (bodyEl) bodyEl.style.display = 'none';
    if (emptyEl) emptyEl.style.display = 'flex';
  }

  setLoading(isLoading) {
    if (!this.drawer) return;
    // Refcount (13-jul): varias cadenas (processCart anidado en swapBundleTier)
    // pueden pedir loading a la vez; el toggle suelto hacía que el finally de
    // una cadena limpiara el estado que otra aún necesitaba.
    this._loadingCount = Math.max(0, (this._loadingCount || 0) + (isLoading ? 1 : -1));
    this.drawer.classList.toggle('is-loading', this._loadingCount > 0);
  }

  showNotification(message, type = 'success') {
    // BIES (17-jun): el cliente pidió eliminar la "caja verde" de avisos del carrito
    // ("Cambiado a pack...", "Añadido", etc.). La desactivamos en el origen para que
    // NO aparezca nunca (independiente de caché/CSS). No afecta a la lógica de
    // packs/descuentos: esto solo pintaba el toast visual.
    return;
  }

  formatMoney(cents) {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(cents / 100);
  }

  parseMoney(text) {
    // Parse "36,98 €" or "36,98\u00a0€" back to cents (3698)
    const cleaned = text.replace(/[^\d,]/g, '').replace(',', '.');
    return Math.round(parseFloat(cleaned) * 100) || 0;
  }

  escapeHtml(text) {
    const map = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    };
    return text.replace(/[&<>"']/g, m => map[m]);
  }
}

// Initialize cart drawer
const biesCartDrawer = new BiesCartDrawer();

// Expose globally for external triggers
window.biesCartDrawer = biesCartDrawer;

// Listen for add-to-cart events from product forms
document.addEventListener('submit', async (e) => {
  const form = e.target;

  // Check if it's a product form - be more aggressive in detection
  const isProductForm = form.getAttribute('action') === '/cart/add' ||
                        form.getAttribute('action')?.includes('/cart/add') ||
                        form.classList.contains('product-form') ||
                        form.querySelector('[name="id"]'); // Has variant ID input

  if (isProductForm) {
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();

    biesDebug('[BIES Cart Drawer] Intercepted add-to-cart form submission');

    const formData = new FormData(form);
    const formId = form.id || 'unknown';

    // Apertura OPTIMISTA (14-jul): el drawer abre AHORA — fuera de la cola
    // serie, antes de cualquier red — con el contenido actual + spinner sutil.
    // El estado autoritativo llega después y el render diffing in-place lo
    // reconcilia sin flash. Antes: el drawer no abría hasta add.js + /cart.js
    // (~600-800ms de "no pasa nada" tras el click).
    biesCartDrawer.open({ skipUpdate: true });
    const skeletonShown = biesCartDrawer._showFirstAddSkeleton();

    // Encolar en la cola serie (misma que +/-/remove) para que un "añadir"
    // no se pise con otra operación de carrito en vuelo.
    biesCartDrawer._serial(async () => {
      try {
        // Colapso de round-trips (14-jul): add.js con sections= devuelve en la
        // MISMA respuesta la section renderizada, de la que extraemos el JSON
        // autoritativo del carrito → sin segundo fetch a /cart.js.
        formData.append('sections', BiesCartDrawer.SECTION_ID);
        const response = await fetch('/cart/add.js', { method: 'POST', body: formData });
        if (!response.ok) throw new Error('Failed to add to cart');

        const cart = await biesCartDrawer._cartFromAddResponse(await response.json());
        await biesCartDrawer.processCart(cart);       // reconcilia dentro de la cola

        if (window.dataLayer) {
          window.dataLayer.push({ event: 'add_to_cart', form_id: formId });
        }
      } catch (error) {
        console.error('[BIES Cart Drawer] Error adding to cart:', error);
        alert('Error al añadir el producto al carrito');
        // Restaurar la VISTA real (deshace skeleton/optimismo si el add falló,
        // p.ej. sin stock). renderCart a propósito — NO processCart: tras un
        // fallo no relanzamos mutaciones (normalize/gift-sync); la próxima op
        // exitosa ya reconcilia.
        try {
          const realCart = await biesCartDrawer._authoritativeCart();
          biesCartDrawer.renderCart(realCart);
        } catch (_) {
          // Doble fallo de red: al menos deshacer el skeleton para no dejar
          // un shimmer eterno sobre un carrito que sigue vacío.
          if (skeletonShown) biesCartDrawer._hideFirstAddSkeleton();
        }
      }
    });

    return false;
  }
}, true); // Use capture phase to intercept earlier

// Intercept cart icon clicks in header
document.addEventListener('click', (e) => {
  const cartIcon = e.target.closest('#cart-icon-bubble');
  if (cartIcon) {
    e.preventDefault();
    e.stopPropagation();
    biesDebug('[BIES Cart Drawer] Cart icon clicked, opening drawer');
    biesCartDrawer.open();
  }
}, true);

// Also intercept any link to /cart to open drawer instead
document.addEventListener('click', (e) => {
  const link = e.target.closest('a[href="/cart"]');
  if (link) {
    e.preventDefault();
    e.stopPropagation();
    biesDebug('[BIES Cart Drawer] Cart link clicked, opening drawer');
    biesCartDrawer.open();
  }
}, true);

biesDebug('[BIES Cart Drawer] Initialized successfully');
